/**
 * Smooth vertex normals for an indexed mesh: each vertex the area-weighted sum
 * of the faces around it, as Three's `computeVertexNormals` does. Computed in
 * the placement worker, so the main thread only wraps the buffers.
 */
export function vertexNormals(positions: Float32Array, indices: ArrayLike<number>): Float32Array {
  const n = new Float32Array(positions.length);
  const p = positions;
  for (let t = 0; t + 2 < indices.length; t += 3) {
    const a = indices[t] * 3, b = indices[t + 1] * 3, c = indices[t + 2] * 3;
    const ux = p[b] - p[a], uy = p[b + 1] - p[a + 1], uz = p[b + 2] - p[a + 2];
    const vx = p[c] - p[a], vy = p[c + 1] - p[a + 1], vz = p[c + 2] - p[a + 2];
    // (c - b) × (a - b) is Three's convention; (b - a) × (c - a) is the same vector.
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    for (const v of [a, b, c]) { n[v] += nx; n[v + 1] += ny; n[v + 2] += nz; }
  }
  for (let i = 0; i < n.length; i += 3) {
    const len = Math.hypot(n[i], n[i + 1], n[i + 2]);
    if (len > 0) { n[i] /= len; n[i + 1] /= len; n[i + 2] /= len; } else n[i + 1] = 1;
  }
  return n;
}
